import * as Sentry from '@sentry/browser';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {createToast} from '../toasts/toast';
import {AGENTIC_WORKERS_MESSAGE} from './agenticWorkersMessages';
import {
	AGENT_RUN_SCOPE,
	AGENT_RUN_STATE,
	type AgentPage,
	type AgentRegistration,
	type AgentResult,
	type AgentRunHandle,
	type AgentRunOutcome,
	type AgentRunner,
	type AgentRunScope,
} from './agentDeclaration';

export type AgentRunSnapshot = {
	isRunning: boolean;
	waitingCount: number;
};

type AgentRunEntry = {
	// Keyed by scope: a run in progress blocks a second run of the same scope only.
	activeRuns: Map<string, AgentRunHandle>;
	// Newest first.
	waitingResults: AgentResult[];
};

type AgentRunListener = () => void;

const entries = new Map<string, AgentRunEntry>();
const listeners = new Map<string, Set<AgentRunListener>>();

const readEntry = (agentId: string): AgentRunEntry =>
	entries.get(agentId) ?? {
		activeRuns: new Map(),
		waitingResults: [],
	};

const writeEntry = (agentId: string, entry: AgentRunEntry): void => {
	entries.set(agentId, entry);
	listeners.get(agentId)?.forEach(listener => listener());
};

const toScopeKey = (scope: AgentRunScope): string =>
	scope.kind === AGENT_RUN_SCOPE.PAGE ? `${scope.kind}:${scope.page}` : `${scope.kind}:${scope.rowId}`;

const replaceActiveRun = (
	activeRuns: Map<string, AgentRunHandle>,
	scopeKey: string,
	handle: AgentRunHandle | null
): Map<string, AgentRunHandle> => {
	const nextActiveRuns = new Map(activeRuns);
	if (handle) {
		nextActiveRuns.set(scopeKey, handle);
	} else {
		nextActiveRuns.delete(scopeKey);
	}
	return nextActiveRuns;
};

// A result produced again replaces its older copy instead of being counted twice.
const mergeResults = (newResults: AgentResult[], waitingResults: AgentResult[]): AgentResult[] => {
	const newResultIds = new Set(newResults.map(result => result.id));
	return [...newResults, ...waitingResults.filter(result => !newResultIds.has(result.id))];
};

export const getAgentRunSnapshot = (agentId: string, scope: AgentRunScope): AgentRunSnapshot => {
	const entry = readEntry(agentId);
	return {
		isRunning: entry.activeRuns.has(toScopeKey(scope)),
		waitingCount: entry.waitingResults.length,
	};
};

export const subscribeToAgentRun = (agentId: string, listener: AgentRunListener): (() => void) => {
	const agentListeners = listeners.get(agentId) ?? new Set<AgentRunListener>();
	agentListeners.add(listener);
	listeners.set(agentId, agentListeners);
	return () => {
		agentListeners.delete(listener);
		if (agentListeners.size === 0 && listeners.get(agentId) === agentListeners) {
			listeners.delete(agentId);
		}
	};
};

export const startAgentRun = (agentId: string, runner: AgentRunner, scope: AgentRunScope): void => {
	const scopeKey = toScopeKey(scope);
	if (readEntry(agentId).activeRuns.has(scopeKey)) {
		return;
	}
	let handle: AgentRunHandle;
	try {
		handle = runner.start(scope);
	} catch (error) {
		// A runner that cannot start leaves the agent idle; it must not throw out of the menu click.
		Sentry.captureException(error);
		return;
	}
	const startedEntry = readEntry(agentId);
	writeEntry(agentId, {...startedEntry, activeRuns: replaceActiveRun(startedEntry.activeRuns, scopeKey, handle)});

	const isActive = (): boolean => readEntry(agentId).activeRuns.get(scopeKey) === handle;

	const finishRun = (outcome: AgentRunOutcome): void => {
		if (!isActive()) {
			return;
		}
		const entry = readEntry(agentId);
		const producedResults = outcome.state === AGENT_RUN_STATE.PRODUCED ? outcome.results : [];
		writeEntry(agentId, {
			activeRuns: replaceActiveRun(entry.activeRuns, scopeKey, null),
			waitingResults: mergeResults(producedResults, entry.waitingResults),
		});
	};

	// A stopped run may reject as it aborts, so only a rejection while the run is active is a failure.
	const failRun = (error: unknown): void => {
		if (!isActive()) {
			return;
		}
		Sentry.captureException(error);
		finishRun({state: AGENT_RUN_STATE.FAILED, results: [], detail: error});
	};

	handle.outcome.then(finishRun, failRun);
};

export const stopAgentRun = (agentId: string, scope: AgentRunScope): boolean => {
	const scopeKey = toScopeKey(scope);
	const entry = readEntry(agentId);
	const handle = entry.activeRuns.get(scopeKey);
	if (!handle) {
		return false;
	}
	writeEntry(agentId, {...entry, activeRuns: replaceActiveRun(entry.activeRuns, scopeKey, null)});
	handle.stop();
	return true;
};

export const getWaitingResults = (agentId: string): AgentResult[] => readEntry(agentId).waitingResults;

export const clearAgentRunStore = (): void => {
	entries.clear();
	listeners.clear();
};

const RUN_STOPPED_TOAST_DURATION_MILLISECONDS = 5000;

export type AgentRunControls = AgentRunSnapshot & {
	startRun: () => void;
	stopRun: () => void;
};

// The run of one agent across one whole page.
export const useAgentRunState = (registration: AgentRegistration, page: AgentPage): AgentRunControls => {
	const {formatMessage} = useIntl();
	const agentId = registration.declaration.id;
	const scope = useMemo<AgentRunScope>(() => ({kind: AGENT_RUN_SCOPE.PAGE, page}), [page]);
	const [snapshot, setSnapshot] = useState<AgentRunSnapshot>(() => getAgentRunSnapshot(agentId, scope));

	useEffect(() => {
		setSnapshot(getAgentRunSnapshot(agentId, scope));
		return subscribeToAgentRun(agentId, () => setSnapshot(getAgentRunSnapshot(agentId, scope)));
	}, [agentId, scope]);

	const startRun = useCallback(
		() => startAgentRun(agentId, registration.runner, scope),
		[agentId, registration.runner, scope]
	);

	const stopRun = useCallback(() => {
		if (stopAgentRun(agentId, scope)) {
			createToast({
				duration: RUN_STOPPED_TOAST_DURATION_MILLISECONDS,
				message: formatMessage({id: AGENTIC_WORKERS_MESSAGE.TOAST_RUN_STOPPED}),
			});
		}
	}, [agentId, scope, formatMessage]);

	return {
		...snapshot,
		startRun,
		stopRun,
	};
};
